import { useEffect, useState } from "react";
import { widgetConfigApi } from "../services/widgetConfigApi";

// A configuracao do widget de um chat, para a transcricao se parecer com a
// conversa que o cliente teve: o tema pinta os baloes e os cartoes, e o locale
// diz em que lingua os cartoes falavam.
//
// Cache ao nivel do modulo, portanto dura a sessao: o operador salta entre
// conversas do mesmo chat na lista da esquerda e o pedido faz-se uma vez so.
// Guarda-se a promessa e nao o valor resolvido, para que duas montagens no
// mesmo tick partilhem um pedido em vez de dispararem dois.
const configByChat = new Map();

const load = (chatId) => {
  if (!configByChat.has(chatId)) {
    configByChat.set(
      chatId,
      widgetConfigApi
        .get(chatId)
        .then((config) => config || null)
        .catch((error) => {
          // As cores sao decoracao, a transcricao e o conteudo. Um 403 de quem
          // nao mexe no widget, ou a rede em baixo, nao pode virar toast nem
          // tapar o que o operador veio ler: devolve-se "sem tema" e o CSS fica
          // com os tokens da casa.
          console.error("widget theme for transcript failed", error);
          // O 403 fica em cache porque e uma RESPOSTA: quem nao mexe no widget
          // nao vai passar a mexer a meio da sessao, e insistir dava um pedido
          // falhado por cada conversa aberta. Tudo o resto sai, senao um
          // soluco de rede de dois segundos deixava este chat sem tema ate ao
          // fim da sessao.
          if (error.response?.status !== 403) configByChat.delete(chatId);
          return null;
        })
    );
  }
  return configByChat.get(chatId);
};

/**
 * Esquece o que esta guardado para um chat, para a leitura seguinte ir buscar
 * de novo. Quem edita o tema do widget chama isto depois de gravar: a cache e
 * ao nivel do modulo e dura a sessao, portanto sem isto o operador que acabou
 * de mudar as cores continuava a ver as antigas na transcricao ate recarregar
 * a pagina inteira.
 *
 * @param {string|undefined} chatId
 */
export const invalidateChatWidgetConfig = (chatId) => {
  configByChat.delete(chatId);
};

/**
 * Comeca a leitura sem esperar por quem a consome. A lista da esquerda sabe o
 * `chat_id` de cada linha antes de a transcricao existir, portanto pode arrancar
 * o pedido no clique em vez de esperar que o detalhe responda primeiro. Sem isto
 * os dois pedidos ficam em serie e o operador ve os baloes na cor de recurso
 * ate ao segundo terminar: medido em 1760ms.
 *
 * @param {string|undefined} chatId
 */
export const prefetchChatWidgetConfig = (chatId) => {
  if (chatId) load(chatId);
};

/**
 * @param {string|undefined} chatId
 * Tres estados, e a distincao entre os dois ultimos e o que evita um flash:
 *   undefined = ainda nao sabemos, a leitura esta em curso
 *   null      = ja sabemos que nao ha tema, o chat nao tem ou o pedido falhou
 *   objeto    = a config
 * Com dois estados so, quem consome nao consegue distinguir "sem tema" de
 * "ainda nao chegou" e pinta a cor de recurso nos dois casos, o que faz os
 * baloes mudarem de cor a meio da leitura.
 *
 * @returns {{theme?: object, locale?: string}|null|undefined}
 */
export const useChatWidgetConfig = (chatId) => {
  const [config, setConfig] = useState(undefined);

  useEffect(() => {
    setConfig(undefined);
    // Sem chat isto e um "ainda nao sabemos" e nao um "nao ha": quem consome
    // monta antes de saber o chat_id (a transcricao so o sabe quando a leitura
    // dela responde) e um `null` aqui punha os baloes na cor de recurso durante
    // esse frame. Mint para cinzento para o azul do tenant e exatamente o
    // flash que os tres estados existem para evitar.
    if (!chatId) return undefined;
    // O chat pode mudar antes da resposta chegar (a lista esta ao lado e o
    // operador clica depressa). Sem esta guarda a resposta do chat anterior
    // pintava a transcricao do chat seguinte.
    let current = true;
    load(chatId).then((value) => {
      if (current) setConfig(value);
    });
    return () => {
      current = false;
    };
  }, [chatId]);

  return config;
};
